import { templateHeaderFormat, templateSlots } from "./rules.js";
/** Compartilhado com `parseMarkdownText` (`markdown-render.ts`): as duas gramáticas usam a MESMA noção de fronteira, e duplicá-la seria convidar as duas a divergirem. */
export const isBoundary = (char) => char === undefined || /^[\s\p{P}]$/u.test(char);
const MARK_KIND = {
    '*': 'bold',
    _: 'italic',
    '~': 'strike',
};
const PLACEHOLDER_PATTERN = /^\{\{(\d+)\}\}/;
/** Junta spans `text` adjacentes — cosmético, não muda o significado da árvore. Compartilhado com `markdown-render.ts`. */
export const mergeAdjacentText = (spans) => {
    const merged = [];
    for (const span of spans) {
        const previous = merged[merged.length - 1];
        if (span.kind === 'text' && previous?.kind === 'text') {
            merged[merged.length - 1] = {
                kind: 'text',
                text: previous.text + span.text,
            };
            continue;
        }
        merged.push(span);
    }
    return merged;
};
/**
 * Interpreta marcadores de formatação (`*negrito*`, `_itálico_`, `~riscado~`,
 * ```mono```) e variáveis não preenchidas (`{{n}}`) num texto JÁ
 * substituído.
 *
 * ## As regras, cada uma com teste em `template-render.test.ts`
 *
 *   - Um marcador só ABRE se precedido de início de string, espaço ou
 *     pontuação; só FECHA se seguido de fim de string, espaço ou pontuação —
 *     a mesma regra que o WhatsApp usa, para que "3*2=6" não vire itálico.
 *   - Um marcador sem par (nunca fechado, ou que nunca teve uma abertura
 *     válida) é literal: o caractere aparece como texto comum.
 *   - Um bloco crase-tripla (```) tem PRECEDÊNCIA sobre tudo — é opaco: nada
 *     dentro dele é interpretado, nem outro marcador nem `{{n}}`. É
 *     encontrado ANTES de qualquer outro marcador, exatamente por isso.
 *   - `{{n}}` que sobrevive até aqui (a variável não foi preenchida) vira
 *     `{kind: 'placeholder'}` — nunca uma string vazia. Um buraco invisível é
 *     pior que um buraco visível: ninguém corrige o que não vê.
 */
export const parseWhatsAppText = (text) => {
    const spans = [];
    let cursor = 0;
    const codeBlock = /```([\s\S]*?)```/g;
    let match;
    while ((match = codeBlock.exec(text)) !== null) {
        if (match.index > cursor) {
            spans.push(...parseMarkersAndPlaceholders(text.slice(cursor, match.index)));
        }
        spans.push({
            kind: 'mono',
            spans: [{ kind: 'code', text: match[1] ?? '' }],
        });
        cursor = match.index + match[0].length;
    }
    if (cursor < text.length) {
        spans.push(...parseMarkersAndPlaceholders(text.slice(cursor)));
    }
    return mergeAdjacentText(spans);
};
/** A parte de `parseWhatsAppText` que roda FORA de um bloco ```, sem nenhum conhecimento dele. */
const parseMarkersAndPlaceholders = (text) => {
    const root = [];
    const stack = [];
    const currentBuffer = () => stack[stack.length - 1]?.buffer ?? root;
    let i = 0;
    let literalRun = '';
    const flushLiteral = () => {
        if (literalRun === '')
            return;
        currentBuffer().push({ kind: 'text', text: literalRun });
        literalRun = '';
    };
    while (i < text.length) {
        const char = text[i];
        const placeholderMatch = PLACEHOLDER_PATTERN.exec(text.slice(i));
        if (placeholderMatch?.[1] !== undefined) {
            flushLiteral();
            currentBuffer().push({
                kind: 'placeholder',
                index: Number(placeholderMatch[1]),
            });
            i += placeholderMatch[0].length;
            continue;
        }
        if (char === '*' || char === '_' || char === '~') {
            const previous = i > 0 ? text[i - 1] : undefined;
            const next = i + 1 < text.length ? text[i + 1] : undefined;
            const canOpen = isBoundary(previous);
            const canClose = isBoundary(next);
            const top = stack[stack.length - 1];
            if (top?.mark === char && canClose) {
                flushLiteral();
                const frame = stack.pop();
                if (frame !== undefined) {
                    const span = {
                        kind: MARK_KIND[frame.mark],
                        spans: mergeAdjacentText(frame.buffer),
                    };
                    currentBuffer().push(span);
                }
                i += 1;
                continue;
            }
            if (canOpen) {
                flushLiteral();
                stack.push({ mark: char, buffer: [] });
                i += 1;
                continue;
            }
            // Nem fecha o topo da pilha, nem pode abrir: o caractere é literal.
            literalRun += char;
            i += 1;
            continue;
        }
        literalRun += char ?? '';
        i += 1;
    }
    flushLiteral();
    // Marcador sem par: qualquer moldura ainda aberta no fim do texto vira
    // literal — o caractere de abertura entra como texto, e o conteúdo já
    // interpretado dentro dela sobe para a moldura de baixo (ou para a raiz).
    while (stack.length > 0) {
        const frame = stack.pop();
        if (frame === undefined)
            continue;
        const parentBuffer = stack[stack.length - 1]?.buffer ?? root;
        parentBuffer.push({ kind: 'text', text: frame.mark });
        parentBuffer.push(...frame.buffer);
    }
    return root;
};
/** Reconstrói o texto plano (com os marcadores de volta) a partir de uma árvore de spans — o inverso exato de `parseWhatsAppText`. */
const spansToText = (spans) => spans.map(spanToText).join('');
const MARK_CHAR = {
    bold: '*',
    italic: '_',
    strike: '~',
};
const spanToText = (span) => {
    switch (span.kind) {
        case 'text':
            return span.text;
        case 'placeholder':
            return `{{${String(span.index)}}}`;
        case 'code':
            return span.text;
        case 'mono':
            return '```' + spansToText(span.spans) + '```';
        case 'bold':
        case 'italic':
        case 'strike': {
            const mark = MARK_CHAR[span.kind];
            return mark + spansToText(span.spans) + mark;
        }
    }
};
/** Um valor ausente ou em branco conta como "não preenchido" — nunca substitui, o `{{n}}` permanece. */
const isMissingValue = (value) => value === undefined || value.trim() === '';
/** Os índices `{{n}}` referenciados no texto de UM componente, em ordem de aparição (com repetição). */
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;
const variableIndexesIn = (text) => Array.from(text.matchAll(VARIABLE_PATTERN)).map((match) => Number(match[1]));
const componentText = (structure, type) => structure.components.find((component) => component.type === type)?.text ??
    null;
/**
 * Substitui as variáveis posicionais de HEADER/BODY pelos valores dados,
 * interpreta a formatação do WhatsApp DEPOIS da substituição (como o
 * WhatsApp de fato faz — se o nome do cliente tiver um `*`, a prévia mostra
 * o estrago exato) e devolve a árvore de spans pronta para
 * `WhatsAppPreview` — nunca HTML.
 *
 * `values` é posicional sobre os SLOTS do modelo (`templateSlots`), não
 * sobre o índice cru — a mesma convenção de `fillTemplateVariables`
 * (`rules.ts`), para que as duas nunca divirjam sobre qual valor vai em qual
 * variável.
 */
export const renderTemplate = (input) => {
    const slots = templateSlots(input.structure);
    const valueForIndex = new Map(slots.map((slot, position) => [slot, input.values[position]]));
    const missing = slots.filter((slot) => isMissingValue(valueForIndex.get(slot)));
    const substitute = (text) => text.replace(VARIABLE_PATTERN, (whole, digits) => {
        const value = valueForIndex.get(Number(digits));
        return isMissingValue(value) ? whole : (value ?? whole);
    });
    const headerFormat = templateHeaderFormat(input.structure);
    // Um cabeçalho de mídia não tem texto para renderizar — o conteúdo é o
    // arquivo, e `text` de um `HEADER` de imagem vem `null` da Meta. Ler o
    // texto mesmo assim (a Meta às vezes devolve `text` vazio) e desenhá-lo
    // faria a prévia mostrar uma linha em branco acima da foto (F066).
    const headerText = headerFormat === 'TEXT' ? componentText(input.structure, 'HEADER') : null;
    const bodyText = componentText(input.structure, 'BODY') ?? '';
    const footerText = componentText(input.structure, 'FOOTER');
    const header = headerText === null ? null : parseWhatsAppText(substitute(headerText));
    const body = parseWhatsAppText(substitute(bodyText));
    // O FOOTER nunca substitui — nem este pacote nem o servidor
    // (`fillTemplateVariables`) tocam nele. Um `{{n}}` ali fica literal, e a
    // prévia mostra o mesmo buraco que o cliente veria.
    const footer = footerText === null ? null : parseWhatsAppText(footerText);
    const warnings = [];
    if (footerText !== null) {
        for (const index of new Set(variableIndexesIn(footerText))) {
            warnings.push({ code: 'variable_in_footer', index });
        }
    }
    const isSequential = slots.every((slot, position) => slot === position + 1);
    if (!isSequential && slots.length > 0) {
        warnings.push({ code: 'non_sequential_slots' });
    }
    const buttonsComponent = input.structure.components.find((component) => component.type === 'BUTTONS');
    return {
        headerFormat,
        header,
        body,
        footer,
        buttons: buttonsComponent?.buttons ?? [],
        missing,
        warnings,
    };
};
/**
 * O texto plano que `communication_message.body` grava — a MESMA árvore de
 * spans que a prévia mostra, nunca uma segunda leitura do template.
 *
 * Antes desta função, a rota (`communication-templates.ts`) fazia o próprio
 * `filled.components.map(text).join('\n')` a partir de
 * `fillTemplateVariables` — um segundo caminho que podia divergir do que a
 * prévia mostrava. Agora os dois nascem da mesma `RenderedTemplate`: o que
 * se grava é, por construção, o que se vê.
 */
export const renderedBodyText = (rendered) => [rendered.header, rendered.body, rendered.footer]
    .filter((block) => block !== null)
    .map(spansToText)
    .filter((text) => text !== '')
    .join('\n');
/**
 * O rótulo de cada SLOT do modelo (`templateSlots`), cruzado com os nomes que
 * `communication_template.variables` guarda — a lista que `WhatsAppPreview`
 * usa para o `title` de um placeholder não preenchido, e que o editor usa
 * para desenhar "Nome do cliente" em vez de "variável 1".
 */
export const templateSlotLabels = (input) => {
    const byIndex = new Map(input.variables.map((variable) => [variable.index, variable]));
    return templateSlots(input.structure).map((index) => {
        const variable = byIndex.get(index);
        return {
            index,
            name: variable?.name ?? null,
            example: variable === undefined || variable.example === ''
                ? null
                : variable.example,
        };
    });
};
//# sourceMappingURL=template-render.js.map